import { Injectable } from '@angular/core';
import { Observable, catchError, forkJoin, map, of, switchMap, throwError } from 'rxjs';
import { TemplateModel } from '../models/template.model';
import { FormDetailModel, FormSettingModel, FormType } from '../models/form.model';
import { FormFieldModel, FormFieldOption } from '../models/form-field.model';
import { MarketingFormsService } from './marketing-forms.service';

// El backend NO expone endpoints dedicados de templates. Los templates son
// MktForm con isTemplate=true. Esta clase encapsula esa convención para que la
// UI siga consumiendo un servicio dedicado.
@Injectable({ providedIn: 'root' })
export class TemplateService {
  constructor(private formsService: MarketingFormsService) {}

  getTemplates(formType?: FormType): Observable<TemplateModel[]> {
    return this.formsService.getForms({ isTemplate: true, formType, size: 200 }).pipe(
      map(page => (page.content ?? []).map(f => ({
        id: f.id,
        name: f.name,
        description: f.internalNotes,
        formType: f.formType,
        status: f.status,
        datetimeCreated: f.datetimeCreated,
        datetimeUpdated: f.datetimeUpdated,
      } as TemplateModel))),
    );
  }

  /**
   * Clona un template en cliente: lee form + fields + options + settings del
   * template y los replica en un nuevo form. El backend no expone un endpoint
   * dedicado de duplicado.
   *
   * Pasos:
   *  1. GET template detail + fields + settings
   *  2. POST nuevo form (DRAFT, isTemplate=false)
   *  3. PUT settings con los del template
   *  4. POST cada field; para tipos con opciones, GET options del original
   *     y POST en el nuevo field.
   */
  /**
   * Crea un nuevo form con isTemplate=true copiando campos, options y settings
   * del form fuente. El form fuente no se modifica.
   */
  saveAsTemplate(sourceFormId: number, templateName: string): Observable<FormDetailModel> {
    return forkJoin({
      source: this.formsService.getFormById(sourceFormId),
      fields: this.formsService.getFields(sourceFormId),
      settings: this.formsService.getSettings(sourceFormId).pipe(catchError(() => of(null))),
    }).pipe(
      switchMap(({ source, fields, settings }) =>
        this.formsService.createForm({
          name: templateName,
          formType: source.formType,
          isTemplate: true,
        }).pipe(
          switchMap(tpl =>
            forkJoin([
              settings
                ? this.formsService.updateSettings(tpl.id, this.sanitizeSettings(settings))
                    .pipe(catchError(() => of(null)))
                : of(null),
              fields.length ? this.cloneFields(sourceFormId, tpl.id, fields) : of(null),
            ]).pipe(map(() => tpl)),
          ),
        ),
      ),
    );
  }

  cloneTemplate(templateId: number, name: string, description?: string): Observable<FormDetailModel> {
    return forkJoin({
      template: this.formsService.getFormById(templateId),
      fields: this.formsService.getFields(templateId),
      settings: this.formsService.getSettings(templateId).pipe(catchError(() => of(null))),
    }).pipe(
      switchMap(({ template, fields, settings }) => {
        if (!template) return throwError(() => new Error('Template no encontrado'));

        return this.formsService.createForm({
          name,
          internalNotes: description?.trim() || undefined,
          formType: template.formType,
          isTemplate: false,
        }).pipe(
          switchMap(newForm => {
            // Try to copy settings from the template via PUT. If the backend
            // hasn't initialised settings for the new form yet, this will 404
            // and be silently ignored.
            const settings$ = settings
              ? this.formsService.updateSettings(newForm.id, this.sanitizeSettings(settings))
                  .pipe(catchError(() => of(null)))
              : of(null);

            const fields$ = fields.length
              ? this.cloneFields(templateId, newForm.id, fields)
              : of(null);

            return forkJoin([settings$, fields$]).pipe(map(() => newForm));
          }),
        );
      }),
    );
  }

  // Copia cada field y, para los que tengan opciones, copia las opciones también.
  private cloneFields(srcFormId: number, dstFormId: number, fields: FormFieldModel[]): Observable<unknown> {
    const ordered = [...fields].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    const ops = ordered.map((f, idx) =>
      this.formsService.createField(dstFormId, { ...this.sanitizeField(f), sortOrder: idx }).pipe(
        switchMap(created => {
          if (!f.id || !this.needsOptions(f.fieldType)) return of(created);
          return this.formsService.getFieldOptions(srcFormId, f.id).pipe(
            catchError(() => of([] as FormFieldOption[])),
            switchMap(opts => {
              if (!opts.length || !created.id) return of(created);
              const optOps = opts.map(o =>
                this.formsService.createFieldOption(dstFormId, created.id!, {
                  optionLabel: o.optionLabel,
                  optionValue: o.optionValue,
                  sortOrder: o.sortOrder,
                  isDefault: o.isDefault,
                  isActive: o.isActive,
                  crmEnumId: o.crmEnumId,
                }).pipe(catchError(() => of(null))),
              );
              return forkJoin(optOps).pipe(map(() => created));
            }),
          );
        }),
      ),
    );
    return forkJoin(ops);
  }

  private needsOptions(t: FormFieldModel['fieldType']): boolean {
    return t === 'DROPDOWN' || t === 'CHECKBOXES' || t === 'RADIO';
  }

  // Removemos id/timestamps/options/crmMap — todo lo demás se replica.
  private sanitizeField(f: FormFieldModel): Partial<FormFieldModel> {
    const { id, formId, datetimeCreated, datetimeUpdated, options, crmMap, ...rest } = f;
    return rest;
  }

  private sanitizeSettings(s: FormSettingModel): Partial<FormSettingModel> {
    const { formId, ...rest } = s;
    return rest;
  }
}
